import { useEffect, useState, useCallback } from "react"
import { createPortal } from "react-dom"
import { DashboardLayout } from "../../layouts/DashboardLayout"
import { http } from "../../shared/api/http"
import { FilterDropdown } from "../../shared/ui/FilterDropdown"
import { ConfirmDialog } from "../../shared/ui/ConfirmDialog"
import {
  listChinaEnterprisesApi,
  getChinaEnterpriseApi,
  createChinaEnterpriseApi,
  updateChinaEnterpriseApi,
  updateChinaEnterpriseStatusApi,
  updateChinaEnterpriseTranslationApi,
  deleteChinaEnterpriseApi,
  uploadEnterpriseLogoApi,
  listChinaCategoriesApi,
  bulkCreateEnterprisesApi,
  bulkDeleteEnterprisesApi,
  BulkCreateResponse,
  EnterpriseItem,
  EnterpriseDetail,
  EnterpriseUpdatePayload,
  EnterpriseType,
  ModerationStatus,
  RelevanceStatus,
  VerificationType,
  ChinaCategory,
} from "../../features/china-enterprises/api/china-enterprises.api"
import {
  TrashIcon,
  MagnifyingGlassIcon,
  BuildingOffice2Icon,
  BuildingStorefrontIcon,
  ArrowsRightLeftIcon,
  Cog6ToothIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ShieldCheckIcon,
  ClockIcon,
  TagIcon,
  PlusIcon,
  PhotoIcon,
  LanguageIcon,
  ArrowUpTrayIcon,
} from "@heroicons/react/16/solid"
import { XMarkIcon, PencilSquareIcon } from "@heroicons/react/20/solid"

const LIMIT = 15
const LANG = "en"

/* ── Moderation config ── */
const MODERATION_BADGE: Record<ModerationStatus, { className: string; dot: string; label: string; gradient: string }> = {
  on_moderation: {
    className: "bg-amber-50 text-amber-700 ring-1 ring-amber-200",
    dot: "bg-amber-400",
    label: "На модерации",
    gradient: "from-amber-500 to-yellow-400",
  },
  approved: {
    className: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200",
    dot: "bg-emerald-500",
    label: "Одобрено",
    gradient: "from-emerald-500 to-green-400",
  },
  rejected: {
    className: "bg-red-50 text-red-700 ring-1 ring-red-200",
    dot: "bg-red-500",
    label: "Отклонено",
    gradient: "from-red-500 to-rose-400",
  },
}

const RELEVANCE_BADGE: Record<RelevanceStatus, { className: string; label: string }> = {
  active:       { className: "bg-blue-50 text-blue-700 ring-1 ring-blue-200",     label: "Актуально" },
  needs_update: { className: "bg-amber-50 text-amber-700 ring-1 ring-amber-200",  label: "Требует обновления" },
  outdated:     { className: "bg-gray-100 text-gray-500 ring-1 ring-gray-200",    label: "Устарело" },
}

const VERIFICATION_BADGE: Record<VerificationType, { className: string; label: string }> = {
  none:   { className: "bg-gray-100 text-gray-500",            label: "Нет" },
  system: { className: "bg-violet-50 text-violet-700",         label: "Система" },
  buyer:  { className: "bg-sky-50 text-sky-700",               label: "Покупатель" },
}

const TYPE_LABELS: Record<EnterpriseType, string> = {
  factory: "Фабрика",
  trading: "Трейдинг",
  manufacturer: "Производитель",
  market: "Рынок",
  other: "Другое",
}

/** Fallback logo icon + color per enterprise type — shown when a firm has no uploaded logo. */
const TYPE_STYLE: Record<EnterpriseType, { icon: typeof BuildingOffice2Icon; bg: string; text: string }> = {
  factory:      { icon: BuildingOffice2Icon,   bg: "bg-blue-50",    text: "text-blue-500" },
  trading:      { icon: ArrowsRightLeftIcon,   bg: "bg-violet-50",  text: "text-violet-500" },
  manufacturer: { icon: Cog6ToothIcon,         bg: "bg-emerald-50", text: "text-emerald-500" },
  market:       { icon: BuildingStorefrontIcon,bg: "bg-amber-50",   text: "text-amber-500" },
  other:        { icon: TagIcon,               bg: "bg-gray-100",   text: "text-gray-400" },
}

const TypeLogoFallback = ({ type, className = "w-8 h-8" }: { type?: EnterpriseType; className?: string }) => {
  const s = (type && TYPE_STYLE[type]) || { icon: BuildingOffice2Icon, bg: "bg-gray-100", text: "text-gray-400" }
  const Icon = s.icon
  return (
    <div className={`${className} rounded-lg ${s.bg} flex items-center justify-center flex-shrink-0`}>
      <Icon className={`w-1/2 h-1/2 ${s.text}`} />
    </div>
  )
}

const ModerationBadge = ({ status }: { status: ModerationStatus }) => {
  const s = MODERATION_BADGE[status] ?? { className: "bg-gray-100 text-gray-500", dot: "bg-gray-400", label: status }
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${s.className}`}>
      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${s.dot}`} />
      {s.label}
    </span>
  )
}

const RelevanceBadge = ({ status }: { status: RelevanceStatus }) => {
  const s = RELEVANCE_BADGE[status] ?? { className: "bg-gray-100 text-gray-500", label: status }
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${s.className}`}>
      {s.label}
    </span>
  )
}

const VerificationBadge = ({ type }: { type: VerificationType }) => {
  const s = VERIFICATION_BADGE[type] ?? { className: "bg-gray-100 text-gray-500", label: type }
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${s.className}`}>
      {s.label}
    </span>
  )
}

/* ── Details form state ── */
type FormState = {
  name: string
  type: EnterpriseType
  description: string
  specialization: string
  production_capacity: string
  address: string
  phone: string
  email: string
  wechat: string
  whatsapp: string
  telegram: string
  category_id: string
  logo_id: string | null
  tags: string[]
  sections: { title: string; items: string }[]
}

const emptyForm: FormState = {
  name: "", type: "trading", description: "", specialization: "", production_capacity: "", address: "",
  phone: "", email: "", wechat: "", whatsapp: "", telegram: "", category_id: "", logo_id: null,
  tags: [], sections: [],
}

const detailToForm = (d: EnterpriseDetail): FormState => ({
  name: d.name,
  type: d.type,
  description: d.description ?? "",
  specialization: d.specialization ?? "",
  production_capacity: d.production_capacity ?? "",
  address: d.address ?? "",
  phone: d.phone ?? "",
  email: d.email ?? "",
  wechat: d.wechat ?? "",
  whatsapp: d.whatsapp ?? "",
  telegram: d.telegram ?? "",
  category_id: d.category_id ?? "",
  logo_id: null,
  tags: d.tags ?? [],
  sections: (d.sections ?? []).map((s) => ({ title: s.title, items: s.items.join(", ") })),
})

const buildUpdatePayload = (original: EnterpriseDetail, form: FormState, logoChanged: boolean): EnterpriseUpdatePayload => {
  const payload: EnterpriseUpdatePayload = {}
  if (form.name !== original.name) payload.name = form.name
  if (form.type !== original.type) payload.type = form.type
  if (form.description !== (original.description ?? "")) payload.description = form.description
  if (form.specialization !== (original.specialization ?? "")) payload.specialization = form.specialization
  if (form.production_capacity !== (original.production_capacity ?? "")) payload.production_capacity = form.production_capacity
  if (form.address !== (original.address ?? "")) payload.address = form.address
  if (form.phone !== (original.phone ?? "")) payload.phone = form.phone
  if (form.email !== (original.email ?? "")) payload.email = form.email
  if (form.wechat !== (original.wechat ?? "")) payload.wechat = form.wechat
  if (form.whatsapp !== (original.whatsapp ?? "")) payload.whatsapp = form.whatsapp
  if (form.telegram !== (original.telegram ?? "")) payload.telegram = form.telegram
  if (form.category_id && form.category_id !== (original.category_id ?? "")) payload.category_id = form.category_id
  if (logoChanged && form.logo_id) payload.logo_id = form.logo_id

  const nextTags = form.tags
  if (JSON.stringify(nextTags) !== JSON.stringify(original.tags ?? [])) {
    payload.tags = nextTags
    if (nextTags.length === 0) payload.clear_tags = true
  }

  const nextSections = form.sections
    .filter((s) => s.title.trim())
    .map((s) => ({ title: s.title.trim(), items: s.items.split(",").map((i) => i.trim()).filter(Boolean) }))
  const originalSections = (original.sections ?? []).map((s) => ({ title: s.title, items: s.items }))
  if (JSON.stringify(nextSections) !== JSON.stringify(originalSections)) {
    payload.sections = nextSections
    if (nextSections.length === 0) payload.clear_sections = true
  }

  return payload
}

export const ChinaEnterprisesPage = () => {
  const [items, setItems] = useState<EnterpriseItem[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)

  const [pendingSearch, setPendingSearch] = useState("")
  const [search, setSearch] = useState("")
  const [moderationFilter, setModerationFilter] = useState<ModerationStatus | "">("")

  const [categories, setCategories] = useState<ChinaCategory[]>([])

  /* Drawer */
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [mode, setMode] = useState<"create" | "edit">("create")
  const [tab, setTab] = useState<"details" | "status" | "translations">("details")
  const [selected, setSelected] = useState<EnterpriseItem | null>(null)
  const [detail, setDetail] = useState<EnterpriseDetail | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)

  const [form, setForm] = useState<FormState>(emptyForm)
  const [logoChanged, setLogoChanged] = useState(false)
  const [logoPreview, setLogoPreview] = useState<string | null>(null)
  const [logoUploading, setLogoUploading] = useState(false)

  const [editModeration, setEditModeration] = useState<ModerationStatus>("on_moderation")
  const [editVerification, setEditVerification] = useState<VerificationType>("none")
  const [editRelevance, setEditRelevance] = useState<RelevanceStatus>("active")

  const [translation, setTranslation] = useState({ name: "", description: "", specialization: "", production_capacity: "" })

  const [saving, setSaving] = useState(false)
  const [saveOk, setSaveOk] = useState(false)
  const [translationSaving, setTranslationSaving] = useState(false)
  const [translationSaveOk, setTranslationSaveOk] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [formError, setFormError] = useState("")

  /* Bulk upload */
  const [bulkOpen, setBulkOpen] = useState(false)
  const [bulkFile, setBulkFile] = useState<File | null>(null)
  const [bulkUploading, setBulkUploading] = useState(false)
  const [bulkResult, setBulkResult] = useState<BulkCreateResponse | null>(null)
  const [bulkError, setBulkError] = useState("")

  /* Bulk delete (select rows → delete by ids) */
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false)
  const [bulkDeleting, setBulkDeleting] = useState(false)
  const [bulkDeleteError, setBulkDeleteError] = useState("")

  /* ── Load ── */
  const load = useCallback(async () => {
    setLoading(true)
    setSelectedIds([])
    try {
      const offset = (page - 1) * LIMIT
      const res = await listChinaEnterprisesApi({
        limit: LIMIT,
        offset,
        ...(moderationFilter ? { moderation_status: moderationFilter } : {}),
        ...(search ? { search } : {}),
      })
      setItems(res.data.enterprises ?? [])
      setTotal(res.data.total ?? res.data.enterprises?.length ?? 0)
    } catch {
      setItems([])
    } finally {
      setLoading(false)
    }
  }, [page, moderationFilter, search])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    listChinaCategoriesApi().then((res) => setCategories(res.data.items ?? [])).catch(() => {})
  }, [])

  const totalPages = Math.max(1, Math.ceil(total / LIMIT))

  const handleSearch = () => { setSearch(pendingSearch); setPage(1) }

  const resetDrawerState = () => {
    setLogoChanged(false)
    setLogoPreview(null)
    setSaveOk(false)
    setTranslationSaveOk(false)
    setConfirmDelete(false)
    setFormError("")
    setTab("details")
  }

  const openCreate = () => {
    setMode("create")
    setSelected(null)
    setDetail(null)
    setForm(emptyForm)
    resetDrawerState()
    setDrawerOpen(true)
  }

  const openEdit = (item: EnterpriseItem) => {
    http.post(`/api/china/enterprises/${item.id}/view`).catch(() => {})
    setMode("edit")
    setSelected(item)
    setDetail(null)
    setForm(emptyForm)
    setEditModeration(item.moderation_status)
    setEditVerification(item.verification_type)
    setEditRelevance(item.relevance_status)
    setTranslation({ name: "", description: "", specialization: "", production_capacity: "" })
    resetDrawerState()
    setDrawerOpen(true)

    setDetailLoading(true)
    getChinaEnterpriseApi(item.id)
      .then((res) => {
        setDetail(res.data)
        setForm(detailToForm(res.data))
        const en = res.data.translations?.[LANG]
        setTranslation({
          name: en?.name ?? "",
          description: en?.description ?? "",
          specialization: en?.specialization ?? "",
          production_capacity: en?.production_capacity ?? "",
        })
      })
      .catch(() => setFormError("Не удалось загрузить данные предприятия"))
      .finally(() => setDetailLoading(false))
  }

  const closeDrawer = () => setDrawerOpen(false)

  const handleLogoUpload = async (file: File) => {
    setLogoPreview(URL.createObjectURL(file))
    setLogoUploading(true)
    try {
      const { data } = await uploadEnterpriseLogoApi(file)
      setForm((f) => ({ ...f, logo_id: data.id }))
      setLogoChanged(true)
    } catch {
      setFormError("Не удалось загрузить логотип")
      setLogoPreview(null)
    } finally {
      setLogoUploading(false)
    }
  }

  const addSection = () => setForm((f) => ({ ...f, sections: [...f.sections, { title: "", items: "" }] }))
  const removeSection = (i: number) => setForm((f) => ({ ...f, sections: f.sections.filter((_, idx) => idx !== i) }))
  const updateSection = (i: number, patch: Partial<{ title: string; items: string }>) =>
    setForm((f) => ({ ...f, sections: f.sections.map((s, idx) => (idx === i ? { ...s, ...patch } : s)) }))

  const [tagDraft, setTagDraft] = useState("")
  const addTag = () => {
    const t = tagDraft.trim()
    if (t && !form.tags.includes(t)) setForm((f) => ({ ...f, tags: [...f.tags, t] }))
    setTagDraft("")
  }
  const removeTag = (t: string) => setForm((f) => ({ ...f, tags: f.tags.filter((x) => x !== t) }))

  const handleSaveDetails = async () => {
    if (!form.name.trim()) { setFormError("Укажите название"); return }
    setSaving(true)
    setFormError("")
    try {
      if (mode === "create") {
        const payload = {
          name: form.name.trim(),
          type: form.type,
          description: form.description || null,
          specialization: form.specialization || null,
          production_capacity: form.production_capacity || null,
          address: form.address || null,
          phone: form.phone || null,
          email: form.email || null,
          wechat: form.wechat || null,
          whatsapp: form.whatsapp || null,
          telegram: form.telegram || null,
          category_id: form.category_id || null,
          logo_id: form.logo_id || null,
          tags: form.tags,
          sections: form.sections
            .filter((s) => s.title.trim())
            .map((s) => ({ title: s.title.trim(), items: s.items.split(",").map((i) => i.trim()).filter(Boolean) })),
        }
        const res = await createChinaEnterpriseApi(payload)
        setSaveOk(true)
        await load()
        // switch straight into edit mode for the freshly created firm, so translations etc. become available
        const asItem: EnterpriseItem = {
          id: res.data.id, name: res.data.name, type: res.data.type, logo: res.data.logo, tags: res.data.tags,
          moderation_status: res.data.moderation_status, verification_type: res.data.verification_type, relevance_status: res.data.relevance_status,
        }
        openEdit(asItem)
      } else if (detail) {
        const payload = buildUpdatePayload(detail, form, logoChanged)
        if (Object.keys(payload).length === 0) {
          setSaveOk(true)
        } else {
          const res = await updateChinaEnterpriseApi(detail.id, payload)
          setDetail(res.data)
          setForm(detailToForm(res.data))
          setLogoChanged(false)
          setSaveOk(true)
          setItems((prev) => prev.map((i) => (i.id === res.data.id ? { ...i, name: res.data.name, type: res.data.type, logo: res.data.logo, tags: res.data.tags } : i)))
        }
      }
    } catch {
      setFormError("Не удалось сохранить изменения")
    } finally {
      setSaving(false)
    }
  }

  const handleSaveStatus = async () => {
    if (!selected) return
    setSaving(true)
    try {
      await updateChinaEnterpriseStatusApi(selected.id, {
        moderation_status: editModeration,
        verification_type: editVerification,
        relevance_status: editRelevance,
      })
      setItems((prev) =>
        prev.map((i) =>
          i.id === selected.id
            ? { ...i, moderation_status: editModeration, verification_type: editVerification, relevance_status: editRelevance }
            : i
        )
      )
      setSelected((prev) => prev ? { ...prev, moderation_status: editModeration, verification_type: editVerification, relevance_status: editRelevance } : null)
      setSaveOk(true)
    } finally {
      setSaving(false)
    }
  }

  const handleSaveTranslation = async () => {
    if (!detail) return
    setTranslationSaving(true)
    setTranslationSaveOk(false)
    try {
      const payload: Record<string, string> = {}
      if (translation.name.trim()) payload.name = translation.name.trim()
      if (translation.description.trim()) payload.description = translation.description.trim()
      if (translation.specialization.trim()) payload.specialization = translation.specialization.trim()
      if (translation.production_capacity.trim()) payload.production_capacity = translation.production_capacity.trim()

      await updateChinaEnterpriseTranslationApi(detail.id, LANG, payload)
      setTranslationSaveOk(true)
    } catch {
      setFormError("Не удалось сохранить перевод")
    } finally {
      setTranslationSaving(false)
    }
  }

  const handleDelete = async () => {
    const id = detail?.id ?? selected?.id
    if (!id) return
    setDeleting(true)
    try {
      await deleteChinaEnterpriseApi(id)
      setItems((prev) => prev.filter((i) => i.id !== id))
      setTotal((t) => Math.max(0, t - 1))
      setDrawerOpen(false)
    } finally {
      setDeleting(false)
    }
  }

  const toggleSelect = (id: string) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))

  const allOnPageSelected = items.length > 0 && items.every((i) => selectedIds.includes(i.id))
  const toggleSelectAll = () =>
    setSelectedIds(allOnPageSelected ? [] : items.map((i) => i.id))

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return
    setBulkDeleting(true)
    setBulkDeleteError("")
    try {
      await bulkDeleteEnterprisesApi(selectedIds)
      setItems((prev) => prev.filter((i) => !selectedIds.includes(i.id)))
      setTotal((t) => Math.max(0, t - selectedIds.length))
      setSelectedIds([])
      setBulkDeleteConfirm(false)
    } catch {
      setBulkDeleteError("Не удалось удалить выбранные предприятия")
    } finally {
      setBulkDeleting(false)
    }
  }

  const openBulk = () => {
    setBulkFile(null)
    setBulkResult(null)
    setBulkError("")
    setBulkOpen(true)
  }
  const closeBulk = () => { if (!bulkUploading) setBulkOpen(false) }

  const handleBulkUpload = async () => {
    if (!bulkFile) return
    setBulkUploading(true)
    setBulkError("")
    try {
      const res = await bulkCreateEnterprisesApi(bulkFile)
      setBulkResult(res.data)
      setBulkFile(null)
      load()
    } catch {
      setBulkError("Не удалось загрузить файл")
    } finally {
      setBulkUploading(false)
    }
  }

  const headerItem = detail ?? selected
  const displayName = mode === "create" ? "Новое предприятие" : headerItem?.name ?? ""
  const displayLogo = logoPreview ?? headerItem?.logo ?? null

  return (
    <DashboardLayout title="Китайские предприятия">

      {/* ── Filter bar ── */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="flex flex-1 min-w-[220px] max-w-sm items-center gap-2 border border-gray-200 rounded-lg px-3 py-2 bg-white">
          <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 shrink-0" />
          <input
            type="text"
            placeholder="Поиск по названию…"
            value={pendingSearch}
            onChange={(e) => setPendingSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            className="flex-1 text-sm outline-none"
          />
        </div>

        <button
          onClick={handleSearch}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition"
        >
          Найти
        </button>

        <FilterDropdown
          label="Статус"
          value={moderationFilter}
          onChange={(v) => { setModerationFilter(v); setPage(1) }}
          options={[
            { value: "on_moderation", label: "На модерации", dot: "bg-amber-400" },
            { value: "approved", label: "Одобрено", dot: "bg-emerald-400" },
            { value: "rejected", label: "Отклонено", dot: "bg-red-400" },
          ]}
        />

        <button
          onClick={openBulk}
          className="ml-auto inline-flex items-center gap-1.5 bg-white border border-gray-200 hover:bg-gray-50 text-gray-600 text-sm font-medium px-4 py-2 rounded-lg transition-colors"
        >
          <ArrowUpTrayIcon className="w-4 h-4" />
          Загрузить из файла
        </button>

        <button
          onClick={openCreate}
          className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
        >
          <PlusIcon className="w-4 h-4" />
          Добавить предприятие
        </button>

        <span className="text-xs text-gray-400">
          {total} предприятий
        </span>
      </div>

      {/* ── Bulk selection bar ── */}
      {selectedIds.length > 0 && (
        <div className="flex items-center justify-between gap-3 mb-3 px-4 py-2.5 rounded-xl bg-blue-50 border border-blue-100">
          <span className="text-sm font-medium text-blue-700">
            Выбрано: {selectedIds.length}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSelectedIds([])}
              className="text-sm font-medium text-blue-600 hover:text-blue-800 transition-colors px-2 py-1"
            >
              Снять выбор
            </button>
            <button
              onClick={() => setBulkDeleteConfirm(true)}
              className="inline-flex items-center gap-1.5 bg-red-600 hover:bg-red-700 text-white text-sm font-medium px-3.5 py-2 rounded-lg transition-colors"
            >
              <TrashIcon className="w-4 h-4" />
              Удалить выбранные
            </button>
          </div>
        </div>
      )}

      {/* ── Table ── */}
      <div className="bg-white rounded-xl border overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-gray-50/40">
              <th className="px-4 py-3 w-10">
                <input
                  type="checkbox"
                  checked={allOnPageSelected}
                  onChange={toggleSelectAll}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
              </th>
              <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Название</th>
              <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Тип</th>
              <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Теги</th>
              <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Модерация</th>
              <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Актуальность</th>
              <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Верификация</th>
              <th className="px-5 py-3 w-20" />
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-50">
            {loading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <tr key={i}>
                  {[1,2,3,4,5,6,7,8].map((j) => (
                    <td key={j} className="px-5 py-4">
                      <div className="h-4 bg-gray-100 rounded animate-pulse w-24" />
                    </td>
                  ))}
                </tr>
              ))
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-5 py-16 text-center">
                  <BuildingOffice2Icon className="w-8 h-8 text-gray-200 mx-auto mb-2" />
                  <p className="text-sm text-gray-400">Предприятий не найдено</p>
                </td>
              </tr>
            ) : (
              items.map((item) => (
                <tr key={item.id} onClick={() => openEdit(item)} className={`hover:bg-gray-50/60 transition-colors cursor-pointer group ${selectedIds.includes(item.id) ? "bg-blue-50/50" : ""}`}>
                  <td className="px-4 py-3.5" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(item.id)}
                      onChange={() => toggleSelect(item.id)}
                      className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-2.5">
                      {item.logo ? (
                        <img src={item.logo} alt={item.name} className="w-8 h-8 rounded-lg object-cover flex-shrink-0" />
                      ) : (
                        <TypeLogoFallback type={item.type} />
                      )}
                      <span className="font-medium text-gray-800 truncate max-w-[180px]">{item.name}</span>
                    </div>
                  </td>
                  <td className="px-5 py-3.5 text-gray-500">{item.type ? TYPE_LABELS[item.type] ?? item.type : <span className="text-gray-300">—</span>}</td>
                  <td className="px-5 py-3.5">
                    <div className="flex flex-wrap gap-1">
                      {item.tags && item.tags.length > 0
                        ? item.tags.slice(0, 3).map((tag) => (
                            <span key={tag} className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs">{tag}</span>
                          ))
                        : <span className="text-gray-300">—</span>}
                    </div>
                  </td>
                  <td className="px-5 py-3.5"><ModerationBadge status={item.moderation_status} /></td>
                  <td className="px-5 py-3.5"><RelevanceBadge status={item.relevance_status} /></td>
                  <td className="px-5 py-3.5"><VerificationBadge type={item.verification_type} /></td>
                  <td className="px-5 py-3.5">
                    <button
                      onClick={(e) => { e.stopPropagation(); openEdit(item) }}
                      className="inline-flex items-center gap-1 text-xs font-medium text-gray-400 hover:text-blue-600 transition-colors px-2 py-1.5 rounded-lg hover:bg-blue-50 opacity-0 group-hover:opacity-100"
                    >
                      <PencilSquareIcon className="w-3.5 h-3.5" />
                      Изменить
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Pagination */}
        {!loading && total > 0 && (
          <div className="flex items-center justify-between px-5 py-3.5 border-t bg-gray-50/40">
            <span className="text-xs text-gray-400">
              Страница <span className="font-medium text-gray-600">{page}</span> из{" "}
              <span className="font-medium text-gray-600">{totalPages}</span>
            </span>
            <div className="flex gap-1.5">
              <button
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
                className="px-3 py-1.5 text-xs font-medium border rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                ← Назад
              </button>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1.5 text-xs font-medium border rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Вперёд →
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Drawer ── */}
      {createPortal(
        <>
          <div
            className={`fixed inset-0 z-40 bg-black/30 backdrop-blur-[2px] transition-opacity duration-300 ${drawerOpen ? "opacity-100" : "opacity-0 pointer-events-none"}`}
            onClick={closeDrawer}
          />
          <div
            className={`fixed top-0 right-0 z-50 h-full w-full max-w-lg bg-white shadow-2xl flex flex-col transition-transform duration-300 ease-out ${drawerOpen ? "translate-x-0" : "translate-x-full"}`}
          >
            {drawerOpen && (
              <>
                {/* ── Header ── */}
                <div className={`bg-gradient-to-r ${mode === "create" ? "from-blue-600 to-blue-500" : MODERATION_BADGE[headerItem?.moderation_status ?? "on_moderation"].gradient} px-6 pt-6 pb-6 flex-shrink-0 relative`}>
                  <button
                    onClick={closeDrawer}
                    className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
                  >
                    <XMarkIcon className="w-4 h-4 text-white" />
                  </button>

                  <div className="flex items-center gap-2 mb-3">
                    <BuildingOffice2Icon className="w-5 h-5 text-white/80" />
                    <span className="text-white/80 text-sm font-medium uppercase tracking-wider">
                      {mode === "create" ? "Новое предприятие" : "Предприятие"}
                    </span>
                  </div>

                  {/* Logo + name */}
                  <div className="flex items-center gap-3 pr-10">
                    <label className="relative w-12 h-12 rounded-xl flex-shrink-0 cursor-pointer group/logo">
                      {displayLogo ? (
                        <img src={displayLogo} alt={displayName} className="w-12 h-12 rounded-xl object-cover ring-2 ring-white/30" />
                      ) : (
                        <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                          {(() => {
                            const Icon = (form.type && TYPE_STYLE[form.type]?.icon) || BuildingOffice2Icon
                            return <Icon className="w-6 h-6 text-white/70" />
                          })()}
                        </div>
                      )}
                      <div className="absolute inset-0 rounded-xl bg-black/40 opacity-0 group-hover/logo:opacity-100 flex items-center justify-center transition-opacity">
                        {logoUploading
                          ? <span className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                          : <PhotoIcon className="w-4 h-4 text-white" />
                        }
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        className="absolute inset-0 opacity-0 cursor-pointer"
                        onChange={(e) => { const f = e.target.files?.[0]; if (f) handleLogoUpload(f) }}
                      />
                    </label>
                    <div className="min-w-0">
                      <p className="text-white font-semibold text-lg leading-tight truncate">{displayName || "Без названия"}</p>
                      {headerItem?.type && mode === "edit" && <p className="text-white/70 text-sm mt-0.5">{TYPE_LABELS[headerItem.type] ?? headerItem.type}</p>}
                    </div>
                  </div>

                  {mode === "edit" && headerItem && (
                    <div className="flex flex-wrap items-center gap-2 mt-4">
                      <ModerationBadge status={headerItem.moderation_status} />
                      <RelevanceBadge status={headerItem.relevance_status} />
                      <VerificationBadge type={headerItem.verification_type} />
                    </div>
                  )}
                </div>

                {/* ── Tabs ── */}
                <div className="flex items-center gap-1 px-6 border-b flex-shrink-0 bg-white">
                  {([
                    ["details", "Детали"],
                    ...(mode === "edit" ? ([["status", "Статус"], ["translations", "Переводы"]] as const) : ([] as const)),
                  ] as const).map(([key, label]) => (
                    <button
                      key={key}
                      onClick={() => setTab(key)}
                      className={`px-3 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
                        tab === key ? "border-blue-600 text-blue-700" : "border-transparent text-gray-400 hover:text-gray-600"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>

                {/* ── Body ── */}
                <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">

                  {mode === "edit" && detailLoading && (
                    <div className="py-10 text-center text-sm text-gray-400">Загрузка…</div>
                  )}

                  {/* ── Details tab ── */}
                  {tab === "details" && (mode === "create" || !detailLoading) && (
                    <div className="space-y-4">
                      <div className="space-y-1.5">
                        <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Название</label>
                        <input
                          value={form.name}
                          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                          placeholder="Chengdu Cosmetics Trading"
                          className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                          <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Тип</label>
                          <select
                            value={form.type}
                            onChange={(e) => setForm((f) => ({ ...f, type: e.target.value as EnterpriseType }))}
                            className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            {(Object.keys(TYPE_LABELS) as EnterpriseType[]).map((t) => (
                              <option key={t} value={t}>{TYPE_LABELS[t]}</option>
                            ))}
                          </select>
                        </div>
                        <div className="space-y-1.5">
                          <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Категория</label>
                          <select
                            value={form.category_id}
                            onChange={(e) => setForm((f) => ({ ...f, category_id: e.target.value }))}
                            className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="">— Без категории —</option>
                            {categories.map((c) => (
                              <option key={c.id} value={c.id}>{c.codename}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="space-y-1.5">
                        <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Описание</label>
                        <textarea
                          value={form.description}
                          onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                          rows={3}
                          className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition resize-none"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                          <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Специализация</label>
                          <input
                            value={form.specialization}
                            onChange={(e) => setForm((f) => ({ ...f, specialization: e.target.value }))}
                            className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                          />
                        </div>
                        <div className="space-y-1.5">
                          <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Мощность производства</label>
                          <input
                            value={form.production_capacity}
                            onChange={(e) => setForm((f) => ({ ...f, production_capacity: e.target.value }))}
                            placeholder="40000 pcs/month"
                            className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                          />
                        </div>
                      </div>

                      <div className="space-y-1.5">
                        <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Адрес</label>
                        <input
                          value={form.address}
                          onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
                          className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                        />
                      </div>

                      {/* Contacts */}
                      <div className="space-y-2">
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Контакты</p>
                        <div className="grid grid-cols-2 gap-3">
                          {([
                            ["phone", "Телефон"], ["email", "Email"], ["wechat", "WeChat"],
                            ["whatsapp", "WhatsApp"], ["telegram", "Telegram"],
                          ] as const).map(([key, label]) => (
                            <input
                              key={key}
                              value={form[key]}
                              onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                              placeholder={label}
                              className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                            />
                          ))}
                        </div>
                      </div>

                      {/* Tags */}
                      <div className="space-y-2">
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                          <TagIcon className="w-3.5 h-3.5" />
                          Теги
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {form.tags.map((tag) => (
                            <span key={tag} className="inline-flex items-center gap-1 px-2.5 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">
                              {tag}
                              <button onClick={() => removeTag(tag)} className="hover:text-red-600">
                                <XMarkIcon className="w-3 h-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                        <div className="flex gap-2">
                          <input
                            value={tagDraft}
                            onChange={(e) => setTagDraft(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag() } }}
                            placeholder="Добавить тег…"
                            className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                          <button onClick={addTag} className="px-3 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">Добавить</button>
                        </div>
                      </div>

                      {/* Sections */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Разделы карточки</p>
                          <button onClick={addSection} className="text-xs font-medium text-blue-600 hover:text-blue-700">+ Добавить раздел</button>
                        </div>
                        {form.sections.length === 0 && <p className="text-xs text-gray-300">Разделов нет</p>}
                        {form.sections.map((s, i) => (
                          <div key={i} className="border border-gray-200 rounded-xl p-3 space-y-2">
                            <div className="flex items-center gap-2">
                              <input
                                value={s.title}
                                onChange={(e) => updateSection(i, { title: e.target.value })}
                                placeholder="Заголовок раздела"
                                className="flex-1 border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                              <button onClick={() => removeSection(i)} className="text-gray-300 hover:text-red-600 flex-shrink-0">
                                <TrashIcon className="w-4 h-4" />
                              </button>
                            </div>
                            <input
                              value={s.items}
                              onChange={(e) => updateSection(i, { items: e.target.value })}
                              placeholder="Пункты через запятую"
                              className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                        ))}
                      </div>

                      {mode === "edit" && (
                        <div className="bg-gray-50 rounded-xl p-4">
                          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">ID</p>
                          <p className="font-mono text-xs text-gray-600 break-all">{detail?.id ?? selected?.id}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* ── Status tab ── */}
                  {tab === "status" && mode === "edit" && selected && (
                    <div className="space-y-5">
                      <div className="space-y-2">
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                          <ClockIcon className="w-3.5 h-3.5" />
                          Статус модерации
                        </p>
                        <div className="grid grid-cols-3 gap-2">
                          {(["on_moderation", "approved", "rejected"] as ModerationStatus[]).map((s) => {
                            const cfg = MODERATION_BADGE[s]
                            const active = editModeration === s
                            return (
                              <button
                                key={s}
                                onClick={() => setEditModeration(s)}
                                className={`flex flex-col items-center gap-1.5 py-3 px-2 rounded-xl border text-xs font-medium transition-all ${
                                  active
                                    ? "border-blue-500 bg-blue-600 text-white shadow-sm shadow-blue-200"
                                    : "border-gray-200 text-gray-500 hover:border-gray-300 hover:bg-gray-50"
                                }`}
                              >
                                <span className={`w-2 h-2 rounded-full ${active ? "bg-white" : cfg.dot}`} />
                                {cfg.label}
                              </button>
                            )
                          })}
                        </div>
                      </div>

                      <div className="space-y-2">
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                          <ShieldCheckIcon className="w-3.5 h-3.5" />
                          Верификация
                        </p>
                        <div className="grid grid-cols-3 gap-2">
                          {(["none", "system", "buyer"] as VerificationType[]).map((v) => {
                            const cfg = VERIFICATION_BADGE[v]
                            const active = editVerification === v
                            return (
                              <button
                                key={v}
                                onClick={() => setEditVerification(v)}
                                className={`py-2.5 px-3 rounded-xl border text-xs font-medium transition-all ${
                                  active
                                    ? "border-blue-500 bg-blue-600 text-white shadow-sm shadow-blue-200"
                                    : "border-gray-200 text-gray-500 hover:border-gray-300 hover:bg-gray-50"
                                }`}
                              >
                                {cfg.label}
                              </button>
                            )
                          })}
                        </div>
                      </div>

                      <div className="space-y-2">
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Актуальность</p>
                        <div className="grid grid-cols-3 gap-2">
                          {(["active", "needs_update", "outdated"] as RelevanceStatus[]).map((r) => {
                            const cfg = RELEVANCE_BADGE[r]
                            const active = editRelevance === r
                            return (
                              <button
                                key={r}
                                onClick={() => setEditRelevance(r)}
                                className={`py-2.5 px-2 rounded-xl border text-xs font-medium transition-all text-center ${
                                  active
                                    ? "border-blue-500 bg-blue-600 text-white shadow-sm shadow-blue-200"
                                    : "border-gray-200 text-gray-500 hover:border-gray-300 hover:bg-gray-50"
                                }`}
                              >
                                {cfg.label}
                              </button>
                            )
                          })}
                        </div>
                      </div>

                      {saveOk && (
                        <div className="flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                          <CheckCircleIcon className="w-4 h-4 flex-shrink-0" />
                          Изменения сохранены
                        </div>
                      )}

                      <button
                        disabled={saving}
                        onClick={handleSaveStatus}
                        className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white py-2.5 rounded-xl text-sm font-semibold transition-colors shadow-sm shadow-blue-200"
                      >
                        {saving
                          ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          : <CheckCircleIcon className="w-4 h-4" />
                        }
                        Сохранить статус
                      </button>

                      {/* ── Delete ── */}
                      <div className="space-y-2 pt-1 border-t">
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider pt-4">Удаление</p>
                        {!confirmDelete ? (
                          <button
                            onClick={() => setConfirmDelete(true)}
                            className="w-full flex items-center justify-center gap-2 bg-red-50 hover:bg-red-100 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm font-medium transition-all"
                          >
                            <TrashIcon className="w-4 h-4" />
                            Удалить предприятие
                          </button>
                        ) : (
                          <div className="border border-red-200 rounded-xl p-4 bg-red-50 space-y-3">
                            <p className="text-sm text-red-700 font-medium">Подтвердите удаление</p>
                            <p className="text-xs text-red-500">Предприятие «{selected.name}» будет удалено навсегда.</p>
                            <div className="flex gap-2">
                              <button
                                onClick={() => setConfirmDelete(false)}
                                className="flex-1 py-2 text-sm font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-white transition-colors"
                              >
                                Отмена
                              </button>
                              <button
                                disabled={deleting}
                                onClick={handleDelete}
                                className="flex-1 flex items-center justify-center gap-1.5 bg-red-600 hover:bg-red-700 disabled:opacity-40 text-white py-2 rounded-lg text-sm font-medium transition-colors"
                              >
                                {deleting
                                  ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                  : <TrashIcon className="w-4 h-4" />
                                }
                                Удалить
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* ── Translations tab ── */}
                  {tab === "translations" && mode === "edit" && detail && (
                    <div className="space-y-4">
                      <div className="flex items-center gap-1.5">
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-50 text-blue-700 text-xs font-semibold">
                          <LanguageIcon className="w-3.5 h-3.5" />
                          EN
                        </span>
                        <span className="text-xs text-gray-400">Другие языки появятся позже</span>
                      </div>

                      <p className="text-xs text-gray-400 leading-relaxed">
                        Незаполненное поле перевода — нормальное состояние: на сайте для этого языка покажется русский оригинал.
                      </p>

                      {([
                        ["name", "Название"],
                        ["description", "Описание"],
                        ["specialization", "Специализация"],
                        ["production_capacity", "Мощность производства"],
                      ] as const).map(([key, label]) => (
                        <div key={key} className="border border-gray-200 rounded-xl p-3.5 space-y-2.5">
                          <p className="text-xs font-semibold text-gray-500">{label}</p>
                          <div className="space-y-1">
                            <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">RU (оригинал)</span>
                            <p className="text-sm text-gray-500 bg-gray-50 rounded-lg px-3 py-2">
                              {(detail[key] as string | null | undefined) || <span className="text-gray-300">—</span>}
                            </p>
                          </div>
                          <div className="space-y-1">
                            <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">EN</span>
                            {key === "description" ? (
                              <textarea
                                value={translation[key]}
                                onChange={(e) => setTranslation((t) => ({ ...t, [key]: e.target.value }))}
                                rows={3}
                                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                              />
                            ) : (
                              <input
                                value={translation[key]}
                                onChange={(e) => setTranslation((t) => ({ ...t, [key]: e.target.value }))}
                                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            )}
                          </div>
                        </div>
                      ))}

                      {translationSaveOk && (
                        <div className="flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                          <CheckCircleIcon className="w-4 h-4 flex-shrink-0" />
                          Перевод сохранён
                        </div>
                      )}

                      <button
                        disabled={translationSaving}
                        onClick={handleSaveTranslation}
                        className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white py-2.5 rounded-xl text-sm font-semibold transition-colors shadow-sm shadow-blue-200"
                      >
                        {translationSaving
                          ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          : <CheckCircleIcon className="w-4 h-4" />
                        }
                        Сохранить перевод
                      </button>
                    </div>
                  )}

                  {formError && (
                    <div className="flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium bg-red-50 text-red-700 border border-red-200">
                      {formError}
                    </div>
                  )}
                </div>

                {/* ── Footer ── */}
                {tab === "details" && (
                  <div className="flex-shrink-0 px-6 py-4 border-t bg-gray-50/80 flex gap-2">
                    <button
                      onClick={closeDrawer}
                      className="flex-1 py-2.5 text-sm font-medium text-gray-600 border border-gray-200 rounded-xl hover:bg-white transition-colors"
                    >
                      Закрыть
                    </button>
                    <button
                      disabled={saving || (mode === "edit" && detailLoading)}
                      onClick={handleSaveDetails}
                      className="flex-1 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white py-2.5 rounded-xl text-sm font-semibold transition-colors shadow-sm shadow-blue-200"
                    >
                      {saving
                        ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        : <CheckCircleIcon className="w-4 h-4" />
                      }
                      {mode === "create" ? "Создать" : "Сохранить"}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </>,
        document.body
      )}

      {/* ── Bulk upload modal ── */}
      {bulkOpen && createPortal(
        <>
          <div className="fixed inset-0 z-40 bg-black/30 backdrop-blur-[2px]" onClick={closeBulk} />
          <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
            <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
              <div className="bg-gradient-to-r from-blue-600 to-blue-500 px-6 py-5 flex items-center gap-2.5">
                <ArrowUpTrayIcon className="w-5 h-5 text-white/90" />
                <h2 className="text-white font-semibold">Загрузить из файла</h2>
              </div>

              <div className="p-6 space-y-4">
                {!bulkResult ? (
                  <>
                    <p className="text-sm text-gray-500 leading-relaxed">
                      Файл <span className="font-mono text-xs bg-gray-100 rounded px-1.5 py-0.5">.csv</span>,{" "}
                      <span className="font-mono text-xs bg-gray-100 rounded px-1.5 py-0.5">.xlsx</span> или{" "}
                      <span className="font-mono text-xs bg-gray-100 rounded px-1.5 py-0.5">.xls</span> с колонками
                      <code className="mx-1">name</code>, <code className="mx-1">type</code>,{" "}
                      <code className="mx-1">category_codename</code> и т.д. Первая строка — заголовки.
                    </p>

                    <label className="block border-2 border-dashed border-gray-200 rounded-xl px-4 py-6 text-center cursor-pointer hover:border-blue-400 transition-colors">
                      <input
                        type="file"
                        accept=".csv,.xlsx,.xls"
                        className="hidden"
                        onChange={(e) => setBulkFile(e.target.files?.[0] ?? null)}
                      />
                      {bulkFile ? (
                        <span className="text-sm font-medium text-gray-700">{bulkFile.name}</span>
                      ) : (
                        <span className="text-sm text-gray-400">Выберите файл или перетащите сюда</span>
                      )}
                    </label>

                    {bulkError && (
                      <div className="flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium bg-red-50 text-red-700 border border-red-200">
                        {bulkError}
                      </div>
                    )}

                    <div className="flex gap-2 pt-1">
                      <button
                        onClick={closeBulk}
                        className="flex-1 py-2.5 text-sm font-medium text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
                      >
                        Отмена
                      </button>
                      <button
                        disabled={!bulkFile || bulkUploading}
                        onClick={handleBulkUpload}
                        className="flex-1 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white py-2.5 rounded-xl text-sm font-semibold transition-colors shadow-sm shadow-blue-200"
                      >
                        {bulkUploading
                          ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          : <ArrowUpTrayIcon className="w-4 h-4" />
                        }
                        Загрузить
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                      <CheckCircleIcon className="w-4 h-4 flex-shrink-0" />
                      Создано записей: {bulkResult.created_count}
                    </div>

                    {bulkResult.errors.length > 0 && (
                      <div className="space-y-2">
                        <p className="flex items-center gap-1.5 text-xs font-semibold text-amber-600 uppercase tracking-wider">
                          <ExclamationTriangleIcon className="w-3.5 h-3.5" />
                          Ошибки ({bulkResult.errors.length})
                        </p>
                        <div className="max-h-56 overflow-y-auto border border-amber-200 bg-amber-50 rounded-xl p-3 space-y-1.5">
                          {bulkResult.errors.map((err, i) => (
                            <p key={i} className="text-xs text-amber-700 leading-relaxed">{err}</p>
                          ))}
                        </div>
                        <p className="text-xs text-gray-400">Поправьте проблемные строки и перезалейте файл — остальные записи уже созданы.</p>
                      </div>
                    )}

                    <button
                      onClick={() => setBulkOpen(false)}
                      className="w-full py-2.5 text-sm font-medium text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
                    >
                      Закрыть
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </>,
        document.body
      )}

      {/* ── Bulk delete confirm ── */}
      <ConfirmDialog
        open={bulkDeleteConfirm}
        title="Удалить выбранные предприятия?"
        message={`Будет удалено ${selectedIds.length} ${selectedIds.length === 1 ? "предприятие" : "предприятий"}. Это действие необратимо.`}
        confirmLabel="Удалить"
        loading={bulkDeleting}
        onConfirm={handleBulkDelete}
        onCancel={() => { if (!bulkDeleting) setBulkDeleteConfirm(false) }}
      />

      {bulkDeleteError && (
        <div className="fixed bottom-5 right-5 z-[95] px-4 py-3 rounded-xl text-sm font-medium bg-red-50 text-red-700 border border-red-200 shadow-lg">
          {bulkDeleteError}
        </div>
      )}
    </DashboardLayout>
  )
}